import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { getLastEstimate, isOnLastEstimate as checkIsOnLastEstimate } from "../utils/lastEstimate";

import { useIsAdmin } from "../auth/AdminContext"; // Setup ▾ is visible to SWA-ESTIMATING-ADMIN members only

// Grouped by the module each item configures, so the Setup dropdown/mobile
// menu reads as "which part of the app is this for" instead of one flat
// unsorted list. Single source for both layouts below (previously two
// independently hand-written lists that had to be kept in sync by hand) and
// for SETUP_PATHS' active-route highlighting.
const SETUP_GROUPS = [
  { label: "Estimating Assistant", items: [
    { icon: "🗂️", label: "Quote Log Setup", sublabel: "Category/subcategory picklist & cost-per-unit associations", path: "/quote-log-setup" },
    { icon: "🚚", label: "Vendors", sublabel: "Vendor directory & their contacts", path: "/vendors" },
  ]},
  { label: "Estimates", items: [
    { icon: "🧰", label: "Estimate Engine", sublabel: "Items, assemblies, rooms, takeoff variables & layers", path: "/estimate-engine" },
    { icon: "💲", label: "Reference Pricing", sublabel: "Master price library & quote-based refresh", path: "/reference-pricing" },
    { icon: "✅", label: "BOD Item Library", sublabel: "Master checklist items & responsibility defaults", path: "/bod-items" },
    { icon: "🧱", label: "Construction Standards", sublabel: "Room-type-pair defaults for auto-detected walls", path: "/construction-standards" },
    { icon: "🧠", label: "Learning", sublabel: "What the takeoff engines learned from estimator decisions — review & approve", path: "/learning" },
    { icon: "📐", label: "Basis of Design", sublabel: "Master mechanical/electrical/architectural design standards & option lists", path: "/basis-of-design" },
  ]},
  { label: "Turnovers", items: [
    { icon: "📇", label: "Turnover Configuration", sublabel: "Default contacts for sign-off & email alerts", path: "/turnover-config" },
  ]},
  { label: "General", items: [
    { icon: "🤖", label: "AI System Prompts", sublabel: "Edit contract, schedule & summary prompts", path: "/system-prompts" },
    { icon: "🏷️", label: "Cost Codes", sublabel: "Master CSI code list used across the app", path: "/cost-codes" },
    { icon: "🔌", label: "Integrations", sublabel: "QBO, ACC & system connections", path: "/integrations" },
    { icon: "📋", label: "Feedback Inbox", sublabel: "Review submitted issues", path: "/help/inbox" },
  ]},
];
const SETUP_PATHS = SETUP_GROUPS.flatMap(g => g.items.map(i => i.path));
const setupItemActive = (path, pathname) =>
  path === "/integrations" ? pathname.startsWith("/integrations") : pathname === path;

export default function Nav({ kbStatus }) {
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const user = accounts[0];
  const isAdmin = useIsAdmin() === true;
  const isMobile = useIsMobile();

  const [setupOpen, setSetupOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const setupRef = useRef(null);

  const initials = user?.name
    ? user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  // "Jump back to the estimate I was just on" — EstimateDetail.jsx writes
  // this on every project/tab visit; re-read on every route change (not just
  // mount) so it's fresh the moment someone wanders off to Setup or the
  // Estimating Assistant. Hidden once they're actually back on that
  // project's page, and no worse than an ordinary top-nav link on a bad
  // read (a private window / cleared storage just never shows it).
  const [lastEstimate, setLastEstimate] = useState(null);
  useEffect(() => {
    setLastEstimate(getLastEstimate());
  }, [location.pathname]);
  const isOnLastEstimate = checkIsOnLastEstimate(lastEstimate, location.pathname);
  const backToEstimateLabel = lastEstimate ? `↩ ${lastEstimate.name}` : null;
  // Help / Report an Issue is an ordinary page in the current tab (it was a new tab 2026-10 -> changed back at Nick's request).
  // The "Back to <estimate>" strip below returns the estimator to where they were.
  const openHelp = () => closeAndNav("/help/feedback");
  // The "Back to <estimate>" strip can be dismissed; it returns the next time an estimate is opened.
  const dismissLastEstimate = () => { try { localStorage.removeItem("cs-last-estimate"); } catch { /* storage blocked — just hide it */ } setLastEstimate(null); };
  const goToLastEstimate = () => lastEstimate && navigate(`/estimates/${lastEstimate.projectId}?section=${lastEstimate.section}`);

  const tools = [
    { path: "/",                   label: "Estimating Assistant" },
    { path: "/estimates",          label: "Estimates" },
    { path: "/executive-dashboard", label: "Dashboard" },
    { path: "/turnover",           label: "Turnovers" },
  ];

  function isActive(path) {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  }

  // Close help dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (setupRef.current && !setupRef.current.contains(e.target)) {
        setSetupOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close dropdown / mobile menu on route change
  useEffect(() => { setSetupOpen(false); setMenuOpen(false); }, [location.pathname]);

  const helpActive = location.pathname === "/help/feedback";
  const setupActive = SETUP_PATHS.some(p => location.pathname.startsWith(p));

  function closeAndNav(path) {
    setSetupOpen(false);
    setMenuOpen(false);
    navigate(path);
  }

  function toolNav(path) {
    setMenuOpen(false);
    navigate(path);
  }

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <div style={{
          background: "var(--cs-navy)", padding: "0 14px", height: 52,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em", minWidth: 0 }}>
            <div style={{ width: 8, height: 8, background: "#4EADFF", borderRadius: "50%", flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>CleanSpace Estimating</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {user && (
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: "var(--cs-blue)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 600, color: "#fff", flexShrink: 0,
              }}>
                {initials}
              </div>
            )}
            <button
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
              style={{
                background: "none", border: "none", cursor: "pointer",
                width: 40, height: 40, display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0,
              }}
            >
              <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
                {menuOpen ? (
                  <path d="M2 2l18 12M20 2L2 14" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                ) : (
                  <>
                    <path d="M1 1h20" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    <path d="M1 8h20" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                    <path d="M1 15h20" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <>
            <div
              onClick={() => setMenuOpen(false)}
              style={{ position: "fixed", inset: 0, top: 52, background: "rgba(0,0,0,0.35)", zIndex: 98 }}
            />
            <div style={{
              position: "fixed", top: 52, left: 0, right: 0, zIndex: 99,
              background: "var(--cs-navy)", borderBottom: "1px solid rgba(255,255,255,0.1)",
              maxHeight: "calc(100vh - 52px)", overflowY: "auto",
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            }}>
              {user && (
                <div style={{
                  padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%", background: "var(--cs-blue)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 600, color: "#fff", flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "#fff", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {user.name || user.username}
                    </div>
                    {kbStatus && (
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: kbStatus === "connected" ? "#5CC96E" : "#9199A6" }} />
                        {kbStatus === "connected" ? "Connected to Azure SQL" : "Not connected"}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ padding: "8px 0" }}>
                {tools.map(t => (
                  <button
                    key={t.path}
                    onClick={() => toolNav(t.path)}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      background: isActive(t.path) ? "rgba(78,173,255,0.12)" : "transparent",
                      border: "none", cursor: "pointer",
                      color: isActive(t.path) ? "#4EADFF" : "rgba(255,255,255,0.8)",
                      fontSize: 15, fontWeight: 500,
                      padding: "13px 18px",
                      borderLeft: isActive(t.path) ? "3px solid #4EADFF" : "3px solid transparent",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
                {lastEstimate && !isOnLastEstimate && (
                  <button
                    onClick={() => { setMenuOpen(false); goToLastEstimate(); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left",
                      background: "transparent", border: "none", cursor: "pointer",
                      color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: 500,
                      padding: "13px 18px", borderLeft: "3px solid transparent",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}
                  >
                    {backToEstimateLabel}
                  </button>
                )}

                <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "8px 0" }} />

                <button
                  onClick={openHelp}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    background: location.pathname === "/help/feedback" ? "rgba(78,173,255,0.12)" : "transparent",
                    border: "none", cursor: "pointer",
                    color: location.pathname === "/help/feedback" ? "#4EADFF" : "rgba(255,255,255,0.8)",
                    fontSize: 15, fontWeight: 500,
                    padding: "13px 18px",
                    borderLeft: location.pathname === "/help/feedback" ? "3px solid #4EADFF" : "3px solid transparent",
                  }}
                >
                  🐛 Report an Issue
                </button>

                {isAdmin && SETUP_GROUPS.map(group => (
                  <div key={group.label}>
                    <div style={{
                      padding: "10px 18px 6px", fontSize: 10, fontWeight: 700,
                      color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.07em",
                    }}>
                      {group.label}
                    </div>
                    {group.items.map(item => {
                      const active = setupItemActive(item.path, location.pathname);
                      return (
                        <button
                          key={item.path}
                          onClick={() => closeAndNav(item.path)}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            background: active ? "rgba(78,173,255,0.12)" : "transparent",
                            border: "none", cursor: "pointer",
                            color: active ? "#4EADFF" : "rgba(255,255,255,0.8)",
                            fontSize: 15, fontWeight: 500,
                            padding: "13px 18px",
                            borderLeft: active ? "3px solid #4EADFF" : "3px solid transparent",
                          }}
                        >
                          {item.icon} {item.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // ── Desktop / tablet layout (unchanged) ─────────────────────────────────────
  return (
    <>
      {/* ── Primary header ── */}
      <div style={{
        background: "var(--cs-navy)", padding: "0 24px", height: 52,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#fff", fontSize: 15, fontWeight: 500, letterSpacing: "-0.01em" }}>
          <div style={{ width: 8, height: 8, background: "#4EADFF", borderRadius: "50%" }} />
          CleanSpace Estimating
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {kbStatus && (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 6, height: 6, borderRadius: "50%",
                background: kbStatus === "connected" ? "#5CC96E" : "#9199A6",
              }} />
              {kbStatus === "connected" ? "Connected to Azure SQL" : "Not connected"}
            </div>
          )}
          {user && (
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%", background: "var(--cs-blue)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 600, color: "#fff",
              }}>
                {initials}
              </div>
              {user.name || user.username}
            </div>
          )}
        </div>
      </div>

      {/* ── Tool navigation ── */}
      <nav style={{
        background: "var(--cs-navy)", borderBottom: "1px solid rgba(255,255,255,0.08)",
        display: "flex", alignItems: "center", padding: "0 24px", height: 40,
        position: "sticky", top: 52, zIndex: 99,
      }}>
        {tools.map(t => (
          <button
            key={t.path}
            onClick={() => navigate(t.path)}
            style={{
              color: isActive(t.path) ? "#4EADFF" : "rgba(255,255,255,0.55)",
              background: "none", border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 500,
              padding: "0 14px", height: 40,
              display: "flex", alignItems: "center",
              borderBottom: isActive(t.path) ? "2px solid #4EADFF" : "2px solid transparent",
              transition: "color 0.15s", whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </button>
        ))}

        {/* ── Help ── */}
        <button
          onClick={openHelp}
          style={{
            color: helpActive ? "#4EADFF" : "rgba(255,255,255,0.55)",
            background: "none", border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 500,
            padding: "0 14px", height: 40,
            display: "flex", alignItems: "center",
            borderBottom: helpActive ? "2px solid #4EADFF" : "2px solid transparent",
            transition: "color 0.15s", whiteSpace: "nowrap",
          }}
        >
          Help
        </button>

        {/* ── Setup dropdown (admin only) ── */}
        {isAdmin && (
          <div ref={setupRef} style={{ position: "relative", height: 40, display: "flex", alignItems: "center" }}>
            <button
              onClick={() => setSetupOpen(o => !o)}
              style={{
                color: setupActive ? "#4EADFF" : "rgba(255,255,255,0.55)",
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 500,
                padding: "0 14px", height: 40,
                display: "flex", alignItems: "center", gap: 5,
                borderBottom: setupActive ? "2px solid #4EADFF" : "2px solid transparent",
                transition: "color 0.15s", whiteSpace: "nowrap",
              }}
            >
              Setup
              <svg width="9" height="6" viewBox="0 0 9 6" fill="none" style={{
                transform: setupOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
                opacity: 0.7,
              }}>
                <path d="M1 1l3.5 3.5L8 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {setupOpen && (
              <div style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                background: "var(--surface)",
                borderRadius: 8,
                boxShadow: "0 4px 24px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)",
                border: "1px solid var(--cs-border)",
                minWidth: 240,
                zIndex: 200,
                maxHeight: "calc(100vh - 112px)",
                overflowY: "auto",
                overflowX: "hidden",
              }}>
                {SETUP_GROUPS.map(group => (
                  <div key={group.label}>
                    <div style={{
                      padding: "8px 14px 4px", fontSize: 10, fontWeight: 700,
                      color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.07em",
                    }}>
                      {group.label}
                    </div>
                    {group.items.map(item => (
                      <DropdownItem
                        key={item.path}
                        icon={item.icon}
                        label={item.label}
                        sublabel={item.sublabel}
                        onClick={() => closeAndNav(item.path)}
                        active={setupItemActive(item.path, location.pathname)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* ── Jump back to the estimate just visited — its own slim strip so a
          long/short project name never changes the tool bar's height or
          width above it ── */}
      {lastEstimate && !isOnLastEstimate && (
        <div style={{
          background: "var(--tint-blue)", borderBottom: "1px solid var(--cs-border)",
          display: "flex", alignItems: "center", padding: "0 24px", height: 28,
          position: "sticky", top: 92, zIndex: 98,
        }}>
          <button
            onClick={goToLastEstimate}
            title={`Back to ${lastEstimate.name}`}
            style={{
              color: "var(--cs-blue)", background: "none", border: "none", cursor: "pointer",
              fontSize: 11.5, fontWeight: 600, padding: 0,
              display: "flex", alignItems: "center", gap: 5,
              maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            <span aria-hidden="true">↩</span> Back to {lastEstimate.name}
          </button>
          <button onClick={dismissLastEstimate} title="Dismiss" aria-label="Dismiss the back-to-estimate link"
            style={{ marginLeft: 8, color: "var(--text3)", background: "none", border: "none", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 4px" }}>✕</button>
        </div>
      )}
    </>
  );
}

function DropdownItem({ icon, label, sublabel, onClick, active }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "9px 14px",
        background: active ? "var(--tint-blue)" : hover ? "#f5f7fa" : "transparent",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        transition: "background 0.1s",
      }}
    >
      <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: active ? "var(--cs-blue)" : "var(--text)", lineHeight: 1.3 }}>
          {label}
        </div>
        {sublabel && (
          <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.3 }}>{sublabel}</div>
        )}
      </div>
    </button>
  );
}
